import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import Pusher, { Channel, ChannelAuthorizationHandler, Options } from 'pusher-js';
import { Observable } from 'rxjs';

/** pusher-js reports a refused channel authorization only as this channel event. */
export const SUBSCRIPTION_ERROR_EVENT = 'pusher:subscription_error';

export class ChannelSubscriptionError extends Error {
    constructor(public readonly channelName: string, public readonly reason: unknown) {
        super(`Subscription to ${channelName} was refused`);
        this.name = 'ChannelSubscriptionError';
    }
}

/**
 * Who authorizes a private channel: the logged-in user's session, or a signing-link token.
 * Each needs its own Pusher connection because a connection has a single authorizer.
 */
export type BroadcastScope =
    | { kind: 'user' }
    | { kind: 'signer'; roleId: number }
    | { kind: 'eform'; templateId: number };

@Injectable({
    providedIn: 'root'
})
export class PusherService {
    private clients = new Map<string, Pusher>();

    constructor(private http: HttpClient) {}

    client(scope: BroadcastScope): Pusher {
        const key = this.scopeKey(scope);
        let client = this.clients.get(key);
        if (!client) {
            client = this.createPusher({
                cluster: environment.cluster,
                channelAuthorization: { customHandler: this.channelAuthorizationHandler(scope) },
            });
            this.clients.set(key, client);
        }
        return client;
    }

    subscribe(scope: BroadcastScope, channelName: string): Channel {
        return this.client(scope).subscribe(channelName);
    }

    unsubscribe(scope: BroadcastScope, channelName: string): void {
        this.client(scope).unsubscribe(channelName);
    }

    listen<T>(scope: BroadcastScope, channelName: string, eventName: string): Observable<T> {
        return new Observable<T>(observer => {
            const channel = this.subscribe(scope, channelName);

            const refused = (reason: unknown) => {
                observer.error(new ChannelSubscriptionError(channelName, reason));
            };
            const handler = (data: T) => {
                observer.next(data);
            };

            channel.bind(SUBSCRIPTION_ERROR_EVENT, refused);
            channel.bind(eventName, handler);

            // Teardown logic on unsubscribe
            return () => {
                channel.unbind(SUBSCRIPTION_ERROR_EVENT, refused);
                channel.unbind(eventName, handler);
                this.unsubscribe(scope, channelName);
            };
        });
    }

    authEndpoint(scope: BroadcastScope): string {
        switch (scope.kind) {
            case 'user':
                return 'broadcasting/auth';
            case 'signer':
                return `sign-document/${scope.roleId}/broadcasting/auth`;
            case 'eform':
                return `eform-signing/${scope.templateId}/broadcasting/auth`;
        }
    }

    channelAuthorizationHandler(scope: BroadcastScope): ChannelAuthorizationHandler {
        // Through HttpClient rather than pusher-js's own XHR, so the API prefix, credentials and signing-token interceptors apply.
        const headers = scope.kind === 'user' ? new HttpHeaders() : new HttpHeaders({ signing_area: '1' });

        return ({ socketId, channelName }, callback) => {
            const endpoint = this.authEndpoint(scope);
            this.http.post<{ auth: string }>(endpoint, { socket_id: socketId, channel_name: channelName }, { headers })
                .subscribe({
                    next: (authData) => callback(null, authData),
                    error: (error) => {
                        // pusher-js only reports a refused subscription behind Pusher.logToConsole, so say it here.
                        console.error(`[PusherService] ${channelName} authorization failed at ${endpoint}`, error);
                        callback(error, null);
                    },
                });
        };
    }

    protected createPusher(options: Options): Pusher {
        return new Pusher(environment.pusherAppKey, options);
    }

    private scopeKey(scope: BroadcastScope): string {
        switch (scope.kind) {
            case 'user':
                return 'user';
            case 'signer':
                return `signer:${scope.roleId}`;
            case 'eform':
                return `eform:${scope.templateId}`;
        }
    }
}
